import { FrameEmptyState } from "@/components/shapes/shared/frame-empty-state";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { useT } from "@/i18n";
import { framesAgendaMessages } from "@/i18n/messages/frames-agenda";
import { cn } from "@/lib/cn";
import { SkeletonReveal, Spinner } from "@/components/ui";
import { PreviewBody } from "@/components/shapes/shared/card-preview-parts";
import {
  DayAccent,
  EventRows,
} from "@/components/shapes/shared/card-visual-parts";
import { CARD_BODY_CONTENT } from "@/components/shapes/shared/frame-content-padding";
import { useFrameSurface } from "../shared/use-frame-surface";
import { dateParts, toAgendaItems } from "./agenda-items";

/**
 * A day's agenda, opened as its card (Part 10): the date accent with the
 * status pill beside it, then the coloured-dot rows, each with its time and
 * detail. Read-only. Content renders from data.content ({date, statusPill?,
 * items[]}); agent frame.update broadcasts re-render it.
 */
export function AgendaFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesAgendaMessages);
  const frameSurface = useFrameSurface(id);
  const content = data.content ?? {};
  const date = typeof content.date === "string" ? content.date : "";
  const statusPill =
    typeof content.statusPill === "string" ? content.statusPill : "";
  const items = toAgendaItems(content.items) ?? [];
  const status = data.status;
  const isEmpty = items.length === 0;

  return (
    <div
      className={cn(
        frameSurface.className,
        "group relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="agenda-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "scrollbar-none min-h-0 flex-1 overflow-y-auto",
          CARD_BODY_CONTENT,
          selected && "nowheel",
        )}
        data-frame-content="true"
        data-frame-drag-handle="true"
        onClick={(e) => e.stopPropagation()}
      >
        <PreviewBody density="regular" className={cn(isEmpty && "h-full")}>
          <SkeletonReveal
            sizeToContent
            loading={status === "streaming" && isEmpty}
            loadingLabel={t("loadingAgenda")}
            data-testid="agenda-frame-reveal"
            contentClassName="gap-[0.9em]"
            skeleton={
              <div
                data-testid="agenda-frame-loading"
                aria-label={t("loadingAgenda")}
              >
                <div className="flex items-center gap-2">
                  <Spinner className="h-3 w-3" />
                  <span className="text-appkit-small text-muted">
                    {t("pullingAgenda")}
                  </span>
                </div>
                <div className="mt-2 flex flex-col gap-1.5" aria-hidden="true">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="h-2.5 w-8 bg-glass-fill-dense rounded" />
                      <div className="h-11 flex-1 bg-glass-fill-dense rounded-control" />
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <>
              {(date || statusPill) && (
                <div
                  className="flex items-start justify-between gap-[0.8em]"
                  data-testid="agenda-frame-header"
                >
                  {date && (
                    <div className="min-w-0" data-testid="agenda-frame-date">
                      <DayAccent {...dateParts(date)} label={date} />
                    </div>
                  )}
                  {statusPill && (
                    <span
                      className="shrink-0 rounded-pill bg-[var(--card-line)] px-[0.6em] py-[0.2em] text-[0.72em] font-semibold tracking-[0.04em] text-card-ink"
                      data-testid="agenda-frame-status-pill"
                    >
                      {statusPill}
                    </span>
                  )}
                </div>
              )}
              {isEmpty ? (
                <FrameEmptyState
                  frameId={id}
                  status={status}
                  testId="agenda-frame-empty"
                >
                  {t("nothingScheduled")}
                </FrameEmptyState>
              ) : (
                <EventRows rows={items} details testId="agenda-frame-item" />
              )}
            </>
          </SkeletonReveal>
        </PreviewBody>
      </div>
    </div>
  );
}
